import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useNavigate } from 'react-router-dom';

// Link nei contenuti (descrizioni, programma, servizi, ecc.):
// - interni (href relativo, o assoluto verso i domini propri) → navigazione SPA via React Router,
//   su web e app nativa: sessione login intatta, nessun browser in-app, nessun reload.
// - esterni http(s) → app nativa: browser in-app (SFSafariViewController / Chrome Custom Tabs),
//   l'utente resta nell'app; web: nuova scheda (target="_blank").
// - schemi non-http (mailto:, tel:, ...) restano al gestore di sistema.
// Il server normalizza già in relativi gli href interni delle descrizioni (rewriteInternalLinks.js);
// il check sui domini propri copre i link assoluti residui (es. campi programma/servizi via getLinks).
// Chiudere il browser in-app resta un no-op per DeepLinkHandler (nessun PENDING_CHECKOUT_KEY).

const isHttpUrl = (href) => /^https?:\/\//i.test(href || '');

const INTERNAL_HOSTNAMES = ['ticinowild.ch', 'www.ticinowild.ch'];

// Restituisce il path interno (pathname + search + hash) se il link punta all'app, altrimenti null.
export const getInternalPath = (href) => {
    if (!href) return null;
    if (href.startsWith('/')) return href; // già relativo (descrizioni post-rewrite server)
    if (!isHttpUrl(href)) return null;     // mailto:, tel:, ecc. → gestore di sistema
    try {
        const url = new URL(href);
        const hostname = url.hostname.toLowerCase();
        // window.location.hostname copre web/deploy-preview sullo stesso dominio (su nativo è localhost,
        // che nei contenuti non compare mai come link assoluto).
        const isOwnHost = INTERNAL_HOSTNAMES.includes(hostname) || hostname === window.location.hostname.toLowerCase();
        if (!isOwnHost) return null;
        return `${url.pathname}${url.search}${url.hash}` || '/';
    } catch {
        return null;
    }
};

const openLink = (event, href, navigate) => {
    const internalPath = getInternalPath(href);
    if (internalPath) {
        event.preventDefault();
        navigate(internalPath);
        return;
    }
    if (Capacitor.isNativePlatform() && isHttpUrl(href)) {
        event.preventDefault();
        Browser.open({ url: href });
    }
    // Esterno su web: comportamento di default dell'anchor.
};

// Hook per i container HTML renderizzati con dangerouslySetInnerHTML (es. descrizioni Quill):
// restituisce l'handler delegato da agganciare come onClick sul contenitore.
export const useContentLinkClick = () => {
    const navigate = useNavigate();
    return (event) => {
        const anchor = event.target.closest('a');
        if (!anchor) return;
        openLink(event, anchor.getAttribute('href'), navigate);
    };
};

const ExternalLink = ({ href, children }) => {
    const navigate = useNavigate();
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={(event) => openLink(event, href, navigate)}>
            {children}
        </a>
    );
};

export default ExternalLink;
